import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import Script from 'next/script'

// Analytics measurement IDs
const ANALYTICS_IDS = {
  MOODLY: "G-YHNXZBG8PS",
  MYROOMDESIGNER: "G-02DGVTGE17"
}

// Domain detection logic (based on MetaTags.jsx) - Only works on client side
const getAnalyticsConfig = (asPath = '') => {
  if (typeof window === 'undefined') return null
  
  // Check if we're on the myroomdesigner.ai domain
  const isMyroomdesignerDomain = window.location.hostname === 'myroomdesigner.ai'
  
  // Check if the path starts with interior-design-ai
  const isInteriorDesignAI = asPath.startsWith("/interior-design-ai/") || asPath.startsWith("/interior-ai-room-designer/")
  
  // Check if it's the main page by checking both pathname and asPath
  const cleanPath = path => path.replace(/\/$/, '').split('?')[0]
  const isInteriorDesignAIMainPage = cleanPath(asPath) === '/interior-design-ai' || cleanPath(asPath) === '/interior-ai-room-designer'
  
  // Check if we're at the root path (/ or empty) on myroomdesigner.ai domain
  const isRootPathOnMyroomdesigner = isMyroomdesignerDomain && (cleanPath(asPath) === "" || cleanPath(asPath) === "/")

  // Check if we should use the MyRoomDesigner branding
  const isMyRoomDesignerSite = isMyroomdesignerDomain || isInteriorDesignAI || isInteriorDesignAIMainPage || isRootPathOnMyroomdesigner

  return {
    measurementId: isMyRoomDesignerSite ? ANALYTICS_IDS.MYROOMDESIGNER : ANALYTICS_IDS.MOODLY,
    isMyRoomDesigner: isMyRoomDesignerSite
  }
}

// Initialize gtag
const initializeGtag = (measurementId) => {
  if (typeof window === 'undefined') return

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || []
  function gtag(){dataLayer.push(arguments)}
  
  // Make gtag globally available
  window.gtag = gtag
  
  // Configure GA4
  gtag('js', new Date())
  gtag('config', measurementId, {
    page_title: document.title,
    page_location: window.location.href
  })
}

// Analytics Scripts Component
export const AnalyticsScripts = () => {
  const router = useRouter()
  const [measurementId, setMeasurementId] = React.useState(null)

  useEffect(() => {
    // Only determine measurement ID on client side
    if (typeof window !== 'undefined') {
      const analyticsConfig = getAnalyticsConfig(router.asPath)
      if (analyticsConfig) {
        setMeasurementId(analyticsConfig.measurementId)
      }
    }
  }, [router.asPath])

  // Don't load analytics in development or if no measurement ID determined yet
  if (!measurementId || process.env.NODE_ENV === 'development') {
    return null
  }

  return (
    <>
      {/* Google Analytics 4 */}
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', '${measurementId}', {
              page_title: document.title,
              page_location: window.location.href
            });
          `,
        }}
      />
    </>
  )
}

// Analytics Hook for tracking page views and events
export const useAnalytics = () => {
  const router = useRouter()

  // Track page views
  useEffect(() => {
    const handleRouteChange = (url) => {
      if (typeof window !== 'undefined' && window.gtag) {
        const analyticsConfig = getAnalyticsConfig(url)
        if (analyticsConfig) {
          window.gtag('config', analyticsConfig.measurementId, {
            page_path: url,
            page_title: document.title,
            page_location: window.location.href
          })
        }
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  // Event tracking function
  const trackEvent = (eventName, parameters = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, parameters)
    } else {
      // Fallback for development or when gtag is not loaded
      console.log('Analytics Event:', eventName, parameters)
    }
  }

  return { trackEvent }
}

// Legacy Firebase Analytics replacement component
export const AnalyticsReporter = () => {
  useAnalytics() // This handles automatic page view tracking
  return null
}

export default AnalyticsScripts 